const TELEGRAM_BACKGROUND = "#F3F1E8";
const TELEGRAM_SDK_ID = "axon-telegram-web-app-sdk";
const TELEGRAM_SDK_URL = "https://telegram.org/js/telegram-web-app.js?59";
const TELEGRAM_SDK_TIMEOUT_MS = 5_000;

let telegramSdkPromise;

function finiteInset(value) {
  return Number.isFinite(value) && value >= 0 ? `${value}px` : "0px";
}

export function telegramWebApp() {
  const webApp = globalThis.Telegram?.WebApp;
  return webApp && typeof webApp.initData === "string" && webApp.initData
    ? webApp
    : null;
}

function telegramLaunchParams(location = globalThis.location) {
  return [location?.search, location?.hash].filter(Boolean).some((segment) => {
    const params = new URLSearchParams(segment.slice(1));
    return params.has("tgWebAppData")
      || params.has("tgWebAppVersion")
      || params.has("tgWebAppPlatform");
  });
}

export function hasTelegramLaunchContext() {
  return telegramWebApp() !== null
    || telegramLaunchParams()
    || Boolean(globalThis.TelegramWebviewProxy)
    || Boolean(globalThis.TelegramWebviewProxyProto);
}

/**
 * Загружает Telegram SDK только внутри Mini App. Обычный web/Android старт
 * никогда не должен ждать внешний telegram.org.
 */
export function loadTelegramSdk({
  documentRef = globalThis.document,
  timeoutMs = TELEGRAM_SDK_TIMEOUT_MS,
} = {}) {
  const current = telegramWebApp();
  if (current) return Promise.resolve(current);
  if (!hasTelegramLaunchContext() || !documentRef?.head) return Promise.resolve(null);
  if (telegramSdkPromise) return telegramSdkPromise;

  telegramSdkPromise = new Promise((resolve) => {
    let settled = false;
    let timer;
    const finish = () => {
      if (settled) return;
      settled = true;
      globalThis.clearTimeout?.(timer);
      resolve(telegramWebApp());
    };

    let script = documentRef.getElementById(TELEGRAM_SDK_ID);
    if (!script) {
      script = documentRef.createElement("script");
      script.id = TELEGRAM_SDK_ID;
      script.src = TELEGRAM_SDK_URL;
      script.async = true;
      script.crossOrigin = "anonymous";
      documentRef.head.append(script);
    }
    script.addEventListener?.("load", finish, { once: true });
    script.addEventListener?.("error", finish, { once: true });
    timer = globalThis.setTimeout?.(finish, timeoutMs);
  });

  return telegramSdkPromise;
}

export function isTelegramMiniApp() {
  return telegramWebApp() !== null;
}

export function telegramInitData() {
  return telegramWebApp()?.initData || null;
}

export function syncTelegramSafeArea(webApp = telegramWebApp()) {
  if (!webApp || !globalThis.document?.documentElement) return;
  const safe = webApp.safeAreaInset || {};
  const content = webApp.contentSafeAreaInset || {};
  const root = globalThis.document.documentElement;
  root.style.setProperty("--tg-safe-top", finiteInset(Math.max(safe.top || 0, content.top || 0)));
  root.style.setProperty("--tg-safe-right", finiteInset(Math.max(safe.right || 0, content.right || 0)));
  root.style.setProperty("--tg-safe-bottom", finiteInset(Math.max(safe.bottom || 0, content.bottom || 0)));
  root.style.setProperty("--tg-safe-left", finiteInset(Math.max(safe.left || 0, content.left || 0)));
}

export function prepareTelegramMiniApp(webApp = telegramWebApp()) {
  if (!webApp) return () => {};
  try {
    webApp.setHeaderColor?.(TELEGRAM_BACKGROUND);
    webApp.setBackgroundColor?.(TELEGRAM_BACKGROUND);
    webApp.setBottomBarColor?.(TELEGRAM_BACKGROUND);
    webApp.expand?.();
    syncTelegramSafeArea(webApp);
    webApp.ready?.();
  } catch {
    // Старые Telegram-клиенты могут поддерживать bridge лишь частично.
  }

  const sync = () => syncTelegramSafeArea(webApp);
  const events = ["safeAreaChanged", "contentSafeAreaChanged", "viewportChanged"];
  for (const event of events) webApp.onEvent?.(event, sync);
  return () => {
    for (const event of events) webApp.offEvent?.(event, sync);
  };
}

export function onTelegramEvent(event, listener) {
  const webApp = telegramWebApp();
  if (!webApp?.onEvent) return () => {};
  webApp.onEvent(event, listener);
  return () => webApp.offEvent?.(event, listener);
}

export function openTelegramExternalLink(url) {
  const webApp = telegramWebApp();
  if (!webApp?.openLink) return false;
  try {
    webApp.openLink(url, { try_instant_view: false });
    return true;
  } catch {
    return false;
  }
}

export function telegramHaptic(kind, style = "light") {
  const haptic = telegramWebApp()?.HapticFeedback;
  if (!haptic) return;
  try {
    if (kind === "success" || kind === "warning" || kind === "error") {
      haptic.notificationOccurred?.(kind);
    } else if (kind === "selection") {
      haptic.selectionChanged?.();
    } else {
      haptic.impactOccurred?.(style);
    }
  } catch {
    // Вибрация — необязательное улучшение, не часть критического пути.
  }
}
